import React, { Component } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Box,
  UnorderedList,
  ListItem,
  Icon
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import colors from '../theme/colors';

class AiConsentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false
    };
  }

  handleOptIn = async () => {
    this.setState({ isSubmitting: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';

      const response = await this.props.authenticatedFetch(`${API_URL}/api/privacy/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.props.username,
          allow_suggestion_summarization: true
        })
      });

      const data = await response.json();


      if (data.success) {
        this.props.showToast({
          title: 'AI-verwerking ingeschakeld',
          description: 'Je suggesties kunnen nu gebruikt worden voor AI-samenvattingen. Je kunt dit altijd wijzigen in Instellingen > AI Verwerking.',
          status: 'success',
          duration: 5000,
          isClosable: true
        });
        this.props.onConsent(true);
        this.props.onClose();
      } else {
        this.props.showToast({
          title: 'Fout bij opslaan',
          description: data.message || 'Er is iets misgegaan',
          status: 'error',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error updating AI consent:', error);
      this.props.showToast({
        title: 'Fout bij opslaan',
        description: 'Kan geen verbinding maken met de server',
        status: 'error',
        duration: 5000
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleOptOut = async () => {
    // User opted out - still need to save to backend that they were prompted
    // This ensures they won't see the modal again
    this.setState({ isSubmitting: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';

      // Update privacy settings with allow_suggestion_summarization = false (opt-out)
      const response = await this.props.authenticatedFetch(`${API_URL}/api/privacy/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.props.username,
          allow_suggestion_summarization: false  // Opt-out
        })
      });

      const data = await response.json();

      if (data.success) {
        this.props.showToast({
          title: 'Keuze opgeslagen',
          description: 'Je hebt ervoor gekozen om AI-verwerking uit te schakelen. Je kunt dit altijd wijzigen in Instellingen > AI Verwerking.',
          status: 'info',
          duration: 6000,
          isClosable: true
        });
        this.props.onConsent(false);
        this.props.onClose();
      } else {
        // If backend fails, still close modal but log error
        console.error('Failed to save opt-out preference:', data.message);
        this.props.showToast({
          title: 'Keuze opgeslagen',
          description: 'Je hebt ervoor gekozen om AI-verwerking uit te schakelen. Je kunt dit altijd wijzigen in Instellingen > AI Verwerking.',
          status: 'info',
          duration: 6000,
          isClosable: true
        });
        this.props.onConsent(false);
        this.props.onClose();
      }
    } catch (error) {
      console.error('Error saving opt-out preference:', error);
      // Still close modal even if backend fails
      this.props.showToast({
        title: 'Keuze opgeslagen',
        description: 'Je hebt ervoor gekozen om AI-verwerking uit te schakelen. Je kunt dit altijd wijzigen in Instellingen > AI Verwerking.',
        status: 'info',
        duration: 6000,
        isClosable: true
      });
      this.props.onConsent(false);
      this.props.onClose();
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { isOpen, onClose } = this.props;
    const { isSubmitting } = this.state;

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Box display="flex" alignItems="center">
              <Icon as={InfoIcon} color={colors.primary.main} mr={2} />
              Help Vertegenwoordigers Begrijpen
            </Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color={colors.text.secondary}>
                Demos gebruikt kunstmatige intelligentie (AI) om samenvattingen te maken van voorstellen van inwoners.
                Dit helpt vertegenwoordigers om patronen en belangrijke thema's te herkennen.
              </Text>

              <Box bg="gray.50" p={4} borderRadius="md">
                <Text fontWeight="600" mb={2} color={colors.primary.dark}>
                  Hoe werkt het?
                </Text>
                <UnorderedList spacing={2} color={colors.text.secondary}>
                  <ListItem>
                    Jouw suggesties worden (anoniem) verwerkt door externe AI-diensten (zoals OpenAI)
                  </ListItem>
                  <ListItem>
                    De AI genereert tekstsamenvattingen van grote hoeveelheden suggesties
                  </ListItem>
                  <ListItem>
                    Vertegenwoordigers zien welke onderwerpen belangrijk zijn voor inwoners
                  </ListItem>
                  <ListItem>
                    Je identiteit blijft beschermd - alleen de inhoud van suggesties wordt gebruikt
                  </ListItem>
                </UnorderedList>
              </Box>

              {/* <Box bg="green.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="green.400">
                <Text fontSize="sm" color="green.900">
                  <strong>Let op:</strong> Trending issues analyse gebeurt via lokale verwerking
                  op onze servers en vereist geen toestemming. Deze toestemming is alleen voor AI-samenvattingen
                  via externe diensten.
                </Text>
              </Box> */}

              <Box bg="orange.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="orange.400">
                <Text fontSize="sm" color="orange.900">
                  <strong>Jouw keuze:</strong> AI-verwerking is optioneel. Je kunt dit altijd aan- of uitzetten
                  in je instellingen onder "Privacy & Gegevens".
                </Text>
              </Box>

              <Text fontWeight="600" color={colors.text.primary}>
                Mogen we jouw suggesties gebruiken voor AI-samenvattingen?
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={this.handleOptOut}
              isLoading={isSubmitting}
              loadingText="Opslaan..."
            >
              Nee, liever niet
            </Button>
            <Button
              bg={colors.button.primary}
              color={colors.text.white}
              _hover={{ bg: colors.button.primaryHover }}
              _active={{ bg: colors.button.primaryActive }}
              onClick={this.handleOptIn}
              isLoading={isSubmitting}
              loadingText="Opslaan..."
            >
              Ja, helpen maar!
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
}

export default AiConsentModal;
